import { type ReactNode, useState } from 'react';
import { Pencil, Check } from 'lucide-react';
import type { UserProfile } from '~/types';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import ButtonDeleteProfile from './DeleteProfile';

interface Props {
  profile: UserProfile;
  canBeDeleted: boolean;
  onProfileDelete: (profile: UserProfile) => void;
  onProfileChange: (profile: UserProfile) => void;
}

export function EditProfile({
  profile,
  canBeDeleted,
  onProfileDelete,
  onProfileChange,
}: Props) {
  const [profileEditing, setProfileEditing] = useState({ ...profile });
  const [isEditing, setIsEditing] = useState(false);
  let editingMarkup: ReactNode;
  const isProfileEditingValid =
    profileEditing.firstName.trim().length >= 2 &&
    profileEditing.lastName.trim().length >= 2;

  if (isEditing) {
    editingMarkup = (
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <Input
            type="text"
            value={profileEditing.firstName}
            onChange={(e) =>
              setProfileEditing({
                ...profileEditing,
                firstName: e.target.value,
              })
            }
          />
          <Input
            type="text"
            value={profileEditing.lastName}
            onChange={(e) =>
              setProfileEditing({ ...profileEditing, lastName: e.target.value })
            }
          />
        </div>
        <div className="flex w-full items-center justify-end gap-4">
          {canBeDeleted && (
            <ButtonDeleteProfile
              profile={profile}
              onProfileDelete={onProfileDelete}
            />
          )}
          <Button
            disabled={!isProfileEditingValid}
            onClick={() => {
              setIsEditing(false);

              const hasChanged = Object.entries(profile).some(
                ([key, value]) => {
                  return profileEditing[key as keyof UserProfile] !== value;
                },
              );

              if (hasChanged) {
                onProfileChange(profileEditing);
              }
            }}
          >
            <Check size={16} /> Save
          </Button>
        </div>
      </div>
    );
  } else {
    editingMarkup = (
      <>
        <div>
          {profileEditing.firstName} {profileEditing.lastName}
        </div>
        <Button variant="secondary" onClick={() => setIsEditing(true)}>
          <Pencil size={16} /> Edit
        </Button>
      </>
    );
  }

  return (
    <div
      key={profile.id}
      className="flex items-center justify-between space-x-2 rounded-md border p-3 font-medium"
    >
      {editingMarkup}
    </div>
  );
}
